<template lang="pug">
ds-panel.tg-chat-identify-modal(space="lg")
  ds-panel-element(offset="bottom")
    ds-caption(
      variant="strong"
      :size="['4']"
    ) {{ modalTitle }}

  ds-panel-element(offset="bottom")
    ds-caption(size="sm") {{ chatInfo }}

  ds-panel-element(
    v-if="!canLinkChat"
    offset="bottom"
  )
    ds-caption.tg-chat-identify-modal__warning(size="sm")
      | {{ _('This chat has no Telegram username or phone, so it cannot be linked automatically.') }}

  ds-panel-element(offset="bottom")
    form-line
      form-cell(cols="12")
        f-select(
          name="action",
          :data="form",
          :super-label="_('Action')",
          label="title",
          track-by="id",
          :allow-empty="false",
          :options="actionOptions"
        )

  template(v-if="actionKey === 'attach'")
    ds-panel-element(
      v-if="suggestionsLoading || suggestionsLoaded || hasSuggestions"
      offset="bottom"
    )
      ds-panel.tg-chat-identify-suggestions(space="sm")
        ds-panel-element(offset="bottom")
          ds-caption(variant="strong") {{ suggestionsTitle }}

          ds-caption(
            v-if="suggestionsMatchedByTitle"
            size="sm"
          ) {{ suggestionsMatchedByTitle }}

        loading-wrapper(:loading="suggestionsLoading" size="xl")
          template(v-if="hasSuggestions")
            .tg-chat-identify-suggestions__list
              .tg-chat-identify-suggestions__item(
                v-for="item in suggestionsList"
                :key="item.id"
                :class="{ 'is-active': form.item && form.item.id === item.id }"
                @click="selectSuggestion(item)"
              )
                ds-caption(variant="strong") {{ getItemTitle(item) }}
                ds-caption(
                  v-if="getItemSubtitle(item)"
                  size="sm"
                ) {{ getItemSubtitle(item) }}

          template(v-else)
            ds-caption(size="sm") {{ _('No recommendations found.') }}
    ds-panel-element(offset="bottom")
      form-line
        form-cell(cols="12")
          f-select(
            name="item",
            :data="form",
            :super-label="isLeadMode ? _('Lead') : _('Client')",
            :label="itemLabel",
            track-by="id",
            :allow-empty="true",
            :preselect-first="false",
            @search-change="$event => { itemsParams.filters.search = $event; receiveItems(itemsParams); }"
            :options="itemsList || []"
            :loading="itemsLoading"
            :readonly="!canLinkChat"
          )
            template(
              #afterList=""
            )
              li
                div(
                  v-if="!itemsLoading && itemsParams.pagination.total > itemsParams.pagination.offset + itemsParams.pagination.limit"
                  v-observe-visibility="visible => getMoreItems(visible)"
                )

    //- ds-panel-element(
    //-   v-if="selectedItemSubtitle"
    //-   offset="bottom"
    //- )
    //-   ds-caption(size="sm") {{ selectedItemSubtitle }}

    ds-panel-element(offset="top")
      form-line
        form-cell(cols="narrow")
          control-button(
            styling="primary"
            :disabled="isSubmitting || !form.item || !canLinkChat"
            @click.prevent="submitAttach"
          ) {{ _('Attach') }}

        form-cell(cols="narrow")
          control-button(
            styling="default"
            :disabled="isSubmitting"
            @click.prevent="close"
          ) {{ _('Cancel') }}

  template(v-else)
    ds-panel-element(offset="bottom")
      ds-caption(size="sm") {{ createHint }}

    ds-panel-element(offset="top")
      form-line
        form-cell(cols="narrow")
          control-button(
            styling="primary"
            :disabled="!canLinkChat"
            @click.prevent="routeToCreate"
          ) {{ createButtonTitle }}

        form-cell(cols="narrow")
          control-button(
            styling="default"
            @click.prevent="close"
          ) {{ _('Cancel') }}
</template>

<script>
import { clone } from 'ramda'

import { clients } from '@md/clients/api'
import { leads } from '@md/leads/api'
import { tgChatApi } from '@md/tg-chat/api'

export default {
  name: 'TelegramChatIdentifyModal',

  props: {
    chat: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      required: true,
      validator(value) {
        return ['lead', 'client'].includes(value)
      },
    },
    createRoute: {
      type: Object,
      default: null,
    },
    promise: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      form: {
        action: {
          id: 'attach',
          title: this._('Attach to existing'),
        },
        item: null,
      },
      itemsList: [],
      itemsLoading: false,
      itemsParams: {
        pagination: {},
        filters: {},
      },
      isSubmitting: false,
      suggestionsList: [],
      suggestionsMatchedBy: null,
      suggestionsLoading: false,
      suggestionsLoaded: false,
    }
  },

  computed: {
    actionOptions() {
      return [
        {
          id: 'attach',
          title: this._('Attach to existing'),
        },
        {
          id: 'create',
          title: this._('Create new'),
        },
      ]
    },

    actionKey() {
      return this.form.action && this.form.action.id
        ? this.form.action.id
        : 'attach'
    },

    isLeadMode() {
      return this.mode === 'lead'
    },

    itemLabel() {
      return this.isLeadMode
        ? '_telegramSelectTitle'
        : 'objId'
    },

    selectedItem() {
      return this.form.item
    },

    // selectedItemSubtitle() {
    //   if (!this.selectedItem) {
    //     return ''
    //   }

    //   return this.getItemSubtitle(this.selectedItem)
    // },

    modalTitle() {
      return this.isLeadMode
        ? this._('Identify Telegram chat as lead')
        : this._('Identify Telegram chat as client')
    },

    createButtonTitle() {
      return this.isLeadMode
        ? this._('Create lead')
        : this._('Create client')
    },

    createHint() {
      return this.isLeadMode
        ? this._('You will be redirected to the lead creation page. Fields will be prefilled from this Telegram chat.')
        : this._('You will be redirected to the client creation page. Fields will be prefilled from this Telegram chat.')
    },

    chatUsername() {
      const username =
        this.chat?.username
        || this.chat?.telegramUsername
        || this.chat?.tgUsername
        || ''

      return username
        ? String(username).replace(/^@/, '')
        : ''
    },

    chatPhone() {
      return (
        this.chat?.phone
        || this.chat?.telegramPhone
        || this.chat?.tgPhone
        || ''
      )
    },

    canLinkChat() {
      return !!(this.chatUsername || this.chatPhone)
    },

    chatInfo() {
      const parts = [
        this.chat?.tgUserId ? `${this._('Telegram ID')}: ${this.chat.tgUserId}` : '',
        this.chatUsername ? `@${this.chatUsername}` : '',
        this.chatPhone,
        this.chat?.firstName || this.chat?.first_name || '',
        this.chat?.lastName || this.chat?.last_name || '',
      ]

      return parts.filter(Boolean).join(' • ')
    },

    searchResource() {
      return this.isLeadMode
        ? leads.list
        : clients.list
    },
    // показываем рекомендации только для attach-сценария.
    hasSuggestions() {
      return !!(
        this.actionKey === 'attach'
        && this.suggestionsList
        && this.suggestionsList.length
      )
    },

    // заголовок блока рекомендаций зависит от типа модалки
    suggestionsTitle() {
      return this.isLeadMode
        ? this._('Recommended leads')
        : this._('Recommended clients')
    },

    // поясняем, по чему backend нашёл совпадения
    suggestionsMatchedByTitle() {
      if (this.suggestionsMatchedBy === 'phone') {
        return this._('Matched by phone')
      }
      if (this.suggestionsMatchedBy === 'name') {
        return this._('Matched by name')
      }
      return ''
    },
  },

  mounted() {
    if (this.canLinkChat) {
      this.receiveItems({ limit: 20 })
      this.receiveSuggestions()
    }
  },

  methods: {
    extractListItems(response) {
      if (Array.isArray(response?.items)) {
        return response.items
      }

      if (Array.isArray(response?.data?.items)) {
        return response.data.items
      }

      if (Array.isArray(response)) {
        return response
      }

      return []
    },

    receiveItems(parameters) {
      const param = clone(parameters)

      if (param.filters) {
        param.filters = JSON.stringify(parameters.filters)
      }

      this.itemsLoading = true

      return this.searchResource.execute(param)
        .then(res => {
          const items = this.extractListItems(res).map(
            item => this.normalizeSelectItem(item)
          )

          if (parameters.offset) {
            this.itemsList.push(...items)
          } else {
            this.itemsList = items
          }

          this.itemsParams.pagination = res.pagination || {}
          this.itemsLoading = false
        })
        .catch(() => {
          this.itemsList = []
          this.itemsLoading = false
          this.$toast.error(this._('Failed to load search results'))
        })
    },
    getMoreItems(isVisible) {
      if (this.itemsLoading || !isVisible) {
        return
      }

      const parameters = {
        ...this.itemsParams,
        limit: this.itemsParams.pagination.limit,
        offset:
          this.itemsParams.pagination.offset + this.itemsParams.pagination.limit,
      }

      this.receiveItems(parameters)
    },

    normalizeSelectItem(item) {
      return {
        ...item,
        _telegramSelectTitle: this.getItemTitle(item),
        _telegramSelectSubtitle: this.getItemSubtitle(item),
      }
    },

    getItemTitle(item) {
      const fullName = [
        item?.name,
        item?.middleName,
        item?.surname,
      ].filter(Boolean).join(' ')

      return (
        item?.title
        || item?.displayName
        || item?.display_name
        || fullName
        || item?.mainPhone
        || item?.main_phone
        || item?.phone
        || item?.email
        || `#${item?.id}`
      )
    },

    getItemSubtitle(item) {
      if (!item) {
        return ''
      }

      const phones = [
        item.mainPhone || item.main_phone,
        item.secondPhone || item.second_phone,
        item.thirdPhone || item.third_phone,
      ].filter(Boolean)

      const parts = [
        item.objId || item.obj_id
          ? `${this._('ID')}: ${item.objId || item.obj_id}`
          : '',
        ...phones,
        item.email || '',
      ]

      return parts.filter(Boolean).join(' • ')
    },

    getLinkPayload() {
      const payload = {
        username: this.chatUsername,
        phone: this.chatPhone,
      }

      if (this.isLeadMode) {
        payload.lead = this.form.item.id
        return payload
      }

      payload.user = this.form.item.id
      return payload
    },

    async submitAttach() {
      if (!this.form.item || !this.canLinkChat || this.isSubmitting) {
        return
      }

      this.isSubmitting = true

      try {
        await tgChatApi.clients.link.execute({}, this.getLinkPayload())

        this.$toast.success(this._('Telegram account linked successfully'))

        if (this.promise) {
          this.promise(true)
        }

        this.$emit('close')
      } catch (error) {
        this.$toast.error(await this.getSubmitError(error))
      } finally {
        this.isSubmitting = false
      }
    },

    routeToCreate() {
      if (!this.canLinkChat || !this.createRoute) {
        return
      }

      this.$emit('close')
      this.$router.push(this.createRoute)
    },

    async receiveSuggestions() {
      if (!this.canLinkChat || this.suggestionsLoading) {
        return
      }

      this.suggestionsLoading = true

      try {
        const response = await tgChatApi.suggestions.list.execute({
          phone: this.chatPhone || '',
          first_name: this.chat?.firstName || this.chat?.first_name || '',
          last_name: this.chat?.lastName || this.chat?.last_name || '',
        })

        const suggestions = this.extractSuggestions(response)

        this.suggestionsMatchedBy =
          suggestions.matchedBy
          || suggestions.matched_by
          || null

        const rawItems = this.isLeadMode
          ? suggestions.leads || []
          : suggestions.clients || []

        this.suggestionsList = rawItems.map(
          item => this.normalizeSelectItem(item)
        )
      } catch (error) {
        this.suggestionsList = []
        this.suggestionsMatchedBy = null
      } finally {
        this.suggestionsLoading = false
        this.suggestionsLoaded = true
      }
    },

    extractSuggestions(response) {
      return (
        response?.data
        || response?.item
        || response
        || {}
      )
    },
    selectSuggestion(item) {
      const normalizedItem = this.normalizeSelectItem(item)

      if (!this.itemsList.some(option => option.id === normalizedItem.id)) {
        this.itemsList = [
          normalizedItem,
          ...this.itemsList,
        ]
      }

      this.form.item = normalizedItem
    },

    close() {
      this.$emit('close')
    },

    async getSubmitError(error) {
      let payload = null

      try {
        if (error?.response?.data) {
          payload = error.response.data
        } else if (error?.data) {
          payload = error.data
        } else if (typeof error?.json === 'function') {
          payload = await error.json()
        } else {
          payload = error
        }
      } catch (parseError) {
        payload = error
      }

      if (typeof payload === 'string') {
        return payload
      }

      const firstError = Array.isArray(payload?.errors)
        ? payload.errors[0]
        : null

      const state = firstError && firstError.state

      if (state && typeof state === 'object') {
        const fieldNames = [
          'nonFieldErrors',
          'username',
          'phone',
          'user',
          'lead',
        ]

        for (const fieldName of fieldNames) {
          const fieldErrors = state[fieldName]

          if (
            Array.isArray(fieldErrors)
            && fieldErrors.length
            && fieldErrors[0].message
          ) {
            return fieldErrors[0].message
          }
        }
      }

      if (typeof firstError?.message === 'string' && firstError.message) {
        return firstError.message
      }

      if (typeof payload?.message === 'string' && payload.message) {
        return payload.message
      }

      return this._('Failed to link Telegram account')
    },
  },
}
</script>
